"use client";

import { ActionGuard } from "@/components/guards/action-guard";
import { Button } from "@/components/rac/button";
import { Input } from "@/components/rac/input";
import { CREATE_OFFER_REQUEST } from "@/gql/request";
import { useAuth } from "@/hooks/use-auth";
import { useBuyerPropertiesStore } from "@/providers/global/stores/buyer-properties-store";
import { PropertyType } from "@/types/property";
import type { SellerUnitAndProject } from "@/types/sup";
import currencify from "@/utils/currency/format";
import { formatRange } from "@/utils/format-range";
import { logger } from "@/utils/logger";
import { toSentenceCase } from "@/utils/string/to-sentence-case";
import { useMutation } from "@apollo/client";
import { X } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { Dialog, Modal, ModalOverlay } from "react-aria-components";
import { toast } from "sonner";

const LAKHS_IN_DIGIT = 100000;

interface MakeOfferModalProps {
	// Common props
	open?: boolean;
	isOpen?: boolean; // Alias for backward compatibility
	onClose: () => void;

	// Option 1: Pass individual IDs (for CustomerCareWidget)
	supId?: string;
	unitId?: string;
	projectId?: string;
	propertyName?: string;
	askingPrice?: number;

	// Option 2: Pass full SUP object (for dashboard cards)
	sup?: SellerUnitAndProject;

	// Optional callback (if provided, will be called instead of making mutation)
	onOfferSubmit?: (price: string) => void;
}

export function MakeOfferModal({
	open,
	isOpen,
	onClose,
	supId,
	unitId,
	projectId,
	propertyName,
	askingPrice,
	sup,
	onOfferSubmit,
}: MakeOfferModalProps) {
	const { user } = useAuth();
	const [createOfferRequest, { loading }] = useMutation(CREATE_OFFER_REQUEST);
	const optimisticAddToShortlist = useBuyerPropertiesStore(state => state.optimisticAddToShortlistManual);

	// Derive values from sup object if provided
	const derivedUnitId = unitId ?? sup?.unitId;
	const derivedProjectId = projectId ?? sup?.projectId;
	const derivedAskingPrice = askingPrice ?? sup?.prices?.[0];

	// Generate property title from sup if not provided
	const title = useMemo(() => {
		if (propertyName) return propertyName;
		if (!sup) return undefined;

		const { bedrooms = [], localityName = "", projectName = "", propertyTypes = [] } = sup;
		const propertyType = toSentenceCase(propertyTypes?.[0] || "apartment");
		const bedroomsStrCompact = formatRange({ data: bedrooms, withSpaces: false });
		return `${bedroomsStrCompact ? `${bedroomsStrCompact}BHK` : propertyType} in ${projectName || localityName || "Bangalore"}`;
	}, [propertyName, sup]);

	const localityName = sup?.localityName || sup?.locality;

	// Price validation (80%-110% of asking price) in lakhs
	const minOfferPriceLakhs = derivedAskingPrice ? (derivedAskingPrice * 0.8) / LAKHS_IN_DIGIT : 0;
	const maxOfferPriceLakhs = derivedAskingPrice ? (derivedAskingPrice * 1.01) / LAKHS_IN_DIGIT : undefined;

	// Convert asking price to lakhs for default value
	const priceInLakhs = derivedAskingPrice ? (derivedAskingPrice / LAKHS_IN_DIGIT).toFixed(2) : "";
	const [offerAmount, setOfferAmount] = useState(priceInLakhs);

	// Parse offer amount (user enters in lakhs, we convert to actual amount)
	const offerInLakhs = parseFloat(offerAmount);
	const offerPrice = !isNaN(offerInLakhs) ? offerInLakhs * LAKHS_IN_DIGIT : 0;
	const isValidOffer = useMemo(() => {
		if (!offerAmount || isNaN(offerInLakhs)) return false;
		if (minOfferPriceLakhs && offerInLakhs < minOfferPriceLakhs) return false;
		if (maxOfferPriceLakhs && offerInLakhs > maxOfferPriceLakhs) return false;
		return true;
	}, [offerAmount, offerInLakhs, minOfferPriceLakhs, maxOfferPriceLakhs]);

	const handleOfferChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		const numericValue = parseFloat(value);

		// Prevent entering amount higher than max cap (in lakhs)
		if (!isNaN(numericValue) && maxOfferPriceLakhs && numericValue > maxOfferPriceLakhs) {
			return;
		}

		setOfferAmount(value);
	};

	const handleSubmit = useCallback(async () => {
		// Validate target ID
		if (!supId && !derivedUnitId && !derivedProjectId) {
			toast.error("No property specified for the offer");
			return;
		}

		if (!isValidOffer) return;

		// If callback is provided, use it instead of mutation
		if (onOfferSubmit) {
			onOfferSubmit(offerPrice.toString());
			onClose();
			return;
		}

		// Otherwise, make the mutation directly
		if (!user?.id) {
			toast.error("Please login to make an offer");
			return;
		}

		try {
			const { data } = await createOfferRequest({
				variables: {
					params: {
						createOfferRequest: {
							price: offerPrice,
							supId: supId || undefined,
							unitId: derivedUnitId || undefined,
							projectId: derivedProjectId || undefined,
						},
					},
				},
			});

			const result = data?.request?.createOfferRequest;

			if (result?.success) {
				// Update the store with the newly created buyerUnit
				// This ensures the UI reflects that the property is now shortlisted
				if (derivedUnitId) {
					optimisticAddToShortlist(derivedUnitId, PropertyType.UNIT);
				} else if (derivedProjectId) {
					optimisticAddToShortlist(derivedProjectId, PropertyType.PROJECT);
				}

				toast.success("Your offer has been submitted successfully!", {
					description: "The seller will be notified of your offer.",
					duration: 5000,
				});
				setOfferAmount(priceInLakhs);
				onClose();
			} else {
				toast.error("Failed to submit offer. Please try again.");
			}
		} catch (e) {
			logger.error("Failed to submit offer:", e);
			toast.error("Failed to submit offer. Please try again.");
		}
	}, [
		supId,
		derivedUnitId,
		derivedProjectId,
		isValidOffer,
		offerPrice,
		onOfferSubmit,
		onClose,
		user?.id,
		createOfferRequest,
		priceInLakhs,
		optimisticAddToShortlist,
	]);

	// Support both 'open' and 'isOpen' props
	const isModalOpen = open ?? isOpen ?? false;

	const priceStr = derivedAskingPrice ? currencify({ amount: derivedAskingPrice, abbreviated: true }) : "";

	return (
		<ModalOverlay
			isOpen={isModalOpen}
			onOpenChange={isOpen => {
				if (!isOpen) onClose();
			}}
			isDismissable
			className="fixed inset-0 z-[10000] bg-black/60 flex items-center justify-center"
		>
			<Modal className="relative bg-white rounded-lg shadow-2xl w-full max-w-md mx-4 overflow-hidden outline-none">
				<Dialog className="outline-none">
					{/* Header */}
					<div className="flex items-center justify-between p-4 border-b">
						<h2 className="text-lg font-semibold text-gray-900">Make Your Offer</h2>
						<Button
							variant="bareshell"
							onPress={onClose}
							className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
						>
							<X size={20} />
						</Button>
					</div>

					{/* Content */}
					<div className="p-4">
						{/* Product Info */}
						<div className="mb-6">
							{title && <h3 className="font-medium text-gray-900 mb-1">{title}</h3>}
							{localityName && <p className="text-sm text-gray-500 mb-2">({localityName})</p>}
							{derivedAskingPrice && (
								<div className="flex items-center justify-between">
									<span className="text-sm text-gray-600">Listed Price:</span>
									<span className="text-lg font-semibold text-green-600">{priceStr}</span>
								</div>
							)}
						</div>

						{/* Offer Input */}
						<div className="mb-6">
							<label htmlFor="offer-amount" className="block text-sm font-medium text-gray-700 mb-2">
								Your Offer Amount (Lakhs)
							</label>
							<Input
								id="offer-amount"
								type="number"
								value={offerAmount}
								onChange={handleOfferChange}
								min={minOfferPriceLakhs.toFixed(2)}
								max={maxOfferPriceLakhs?.toFixed(2)}
								step="0.5"
								placeholder="Enter amount in lakhs"
							/>
							<div className="w-full flex justify-between">
								{offerAmount && !isNaN(offerInLakhs) && (
									<p className="text-xs text-gray-500 mt-1">
										Offer price:&nbsp; {currencify({ amount: parseFloat(offerPrice.toFixed(2)) })}
									</p>
								)}
								{offerAmount && !isNaN(offerInLakhs) && !isValidOffer && (
									<div className="mt-1">
										<p className="text-xs text-red-500">
											Allowed range:{" "}
											{currencify({ amount: parseFloat((minOfferPriceLakhs * LAKHS_IN_DIGIT).toFixed(2)) })} -{" "}
											{currencify({
												amount: parseFloat(((maxOfferPriceLakhs || 0) * LAKHS_IN_DIGIT).toFixed(2)),
											})}
										</p>
									</div>
								)}
							</div>
						</div>

						{/* Submit Button */}
						<div className="w-full flex justify-center">
							<ActionGuard actionId="submit_offer">
								<button
									onClick={handleSubmit}
									disabled={!isValidOffer || loading}
									className={`w-auto m-auto font-medium py-2 px-4 rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
										isValidOffer && !loading
											? "bg-primary-500 hover:bg-primary-600 text-white"
											: "bg-gray-300 text-gray-500 cursor-not-allowed"
									}`}
								>
									{loading ? "Submitting..." : "Submit Offer"}
								</button>
							</ActionGuard>
						</div>
					</div>
				</Dialog>
			</Modal>
		</ModalOverlay>
	);
}

export default MakeOfferModal;
